import React, { Component } from "react";
import { Grid, Row, Col, FormGroup, ControlLabel, FormControl } from "react-bootstrap";
import ReactTable from "react-table";
import "react-table/react-table.css";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import _ from "lodash";
import moment from "moment";

import config from "config";
import Card from "components/Card/Card.jsx";
import Button from "components/CustomButton/CustomButton.jsx";
import { seatBlockService } from "../../services/seat-block.services";

const TYPE_LABELS = {
  maintenance: "Maintenance",
  special_reservation: "Special Reservation",
  vip: "VIP",
  other: "Other",
};

function titleCase(str) {
  if (!str) return "";
  return String(str).replace(/\b\w/g, c => c.toUpperCase());
}

function formatTime(t) {
  if (!t) return "—";
  const m = moment(t, ["HH:mm:ss", moment.ISO_8601]);
  return m.isValid() ? m.format("h:mm A") : t;
}

class SeatBlocks extends Component {
  constructor(props) {
    super(props);
    this.state = {
      blocks: [],
      total: 0,
      loading: false,
      filters: {
        slot: "",
        type: "",
      },
      criteria: {
        limit: 20,
        offset: 0,
        sorted: "",
      },
      error: null,
    };
    this.fetchData = this.fetchData.bind(this);
    this.fetchDataWithDebounce = _.debounce(this.fetchData, 300);
    this.handleFilterChange = this.handleFilterChange.bind(this);
    this.handleApplyFilters = this.handleApplyFilters.bind(this);
    this.handleClearFilters = this.handleClearFilters.bind(this);
    this.unblock = this.unblock.bind(this);
  }

  fetchData(state, instance) {
    // Normalize ReactTable state into criteria
    const criteria = { ...this.state.criteria };
    const newSorted = state && state.sorted && state.sorted.length > 0 ? JSON.stringify(state.sorted) : "";
    if (criteria.sorted !== newSorted) {
      criteria.offset = 0;
      criteria.sorted = newSorted;
    }
    if (state && state.pageSize && criteria.limit !== state.pageSize) {
      criteria.limit = state.pageSize;
      criteria.offset = 0;
    } else if (state) {
      criteria.offset = Math.max(0, Math.ceil((state.page || 0) * (state.pageSize || criteria.limit)));
    }

    this.setState({ criteria, loading: true, error: null }, () => {
      const params = {
        ...this.state.filters,
        limit: criteria.limit,
        offset: criteria.offset,
        sorted: criteria.sorted,
      };
      seatBlockService.list(params)
        .then(data => {
          this.setState({
            blocks: data.blocks || [],
            total: typeof data.total === "number" ? data.total : (data.blocks || []).length,
            loading: false,
          });
        })
        .catch(err => {
          const message = (err && err.error) || "Failed to load seat blocks";
          this.setState({ loading: false, error: message });
        });
    });
  }

  refetchFromStart() {
    // Reset offset and re-fetch with the current criteria + filters.
    const criteria = { ...this.state.criteria, offset: 0 };
    this.setState({ criteria }, () => {
      this.fetchData({ pageSize: criteria.limit, page: 0, sorted: criteria.sorted ? JSON.parse(criteria.sorted) : [] });
    });
  }

  handleFilterChange(e) {
    const { name, value } = e.target;
    this.setState({ filters: { ...this.state.filters, [name]: value } });
  }

  handleApplyFilters() {
    this.refetchFromStart();
  }

  handleClearFilters() {
    this.setState({ filters: { slot: "", type: "" } }, () => this.refetchFromStart());
  }

  unblock(block) {
    const trip = `${titleCase(block.depart_city || "?")} → ${titleCase(block.arrive_city || "?")} ${block.bus_letter ? `(Bus ${block.bus_letter})` : ""}`.trim();
    confirmAlert({
      title: "Unblock Seat",
      message: `Unblock seat ${block.seat_number} on ${moment(block.slot).format("YYYY-MM-DD")} — ${trip}?`,
      buttons: [
        {
          label: "Yes",
          onClick: () => {
            seatBlockService.delete(block.id)
              .then(() => this.refetchFromStart())
              .catch(err => {
                const message = (err && err.error) || "Failed to unblock seat";
                this.setState({ error: message });
              });
          },
        },
        { label: "No", onClick: () => "" },
      ],
    });
  }

  renderFilters() {
    const { filters } = this.state;
    return (
      <Row>
        <Col md={3}>
          <FormGroup>
            <ControlLabel>Date</ControlLabel>
            <FormControl type="date" name="slot" value={filters.slot} onChange={this.handleFilterChange} />
          </FormGroup>
        </Col>
        <Col md={3}>
          <FormGroup>
            <ControlLabel>Type</ControlLabel>
            <FormControl componentClass="select" name="type" value={filters.type} onChange={this.handleFilterChange}>
              <option value="">All</option>
              <option value="maintenance">Maintenance</option>
              <option value="special_reservation">Special Reservation</option>
              <option value="other">Other</option>
            </FormControl>
          </FormGroup>
        </Col>
        <Col md={6} style={{ paddingTop: 25 }}>
          <Button bsStyle="info" fill onClick={this.handleApplyFilters}>Apply</Button>
          {" "}
          <Button bsStyle="default" onClick={this.handleClearFilters}>Clear</Button>
        </Col>
      </Row>
    );
  }

  render() {
    const { blocks, loading, error, total, criteria } = this.state;
    const pageCount = Math.max(1, Math.ceil(total / (criteria.limit || 20)));
    const columns = [
      {
        Header: "Date",
        accessor: "slot",
        Cell: row => moment(row.original.slot).format("YYYY-MM-DD"),
        width: 110,
      },
      {
        Header: "Route",
        id: "depart_city",
        sortable: true,
        Cell: row => {
          const dep = titleCase(row.original.depart_city || "");
          const arr = titleCase(row.original.arrive_city || "");
          return dep || arr ? `${dep || "?"} → ${arr || "?"}` : "—";
        },
        minWidth: 180,
      },
      {
        Header: "Departure",
        accessor: "depart_time",
        Cell: row => formatTime(row.original.depart_time),
        width: 110,
      },
      {
        Header: "Bus",
        id: "bus_letter",
        sortable: true,
        Cell: row => {
          const letter = row.original.bus_letter || "—";
          const t = row.original.bus_type || "";
          return t ? `${letter} (${t})` : letter;
        },
        width: 120,
      },
      {
        Header: "Seat #",
        accessor: "seat_number",
        width: 80,
      },
      {
        Header: "Type",
        accessor: "type",
        Cell: row => TYPE_LABELS[row.original.type] || row.original.type,
        width: 160,
      },
      {
        Header: "Notes",
        accessor: "notes",
        sortable: false,
        Cell: row => row.original.notes || "—",
        minWidth: 140,
      },
      {
        Header: "Created At",
        accessor: "created_at",
        Cell: row => row.original.created_at ? moment(row.original.created_at).format("YYYY-MM-DD HH:mm") : "—",
        width: 150,
      },
      {
        Header: "Actions",
        sortable: false,
        filterable: false,
        width: 130,
        style: { paddingRight: 16 },
        headerStyle: { paddingRight: 16 },
        Cell: row => (
          <div style={{ paddingRight: 8 }}>
            <Button bsStyle="danger" fill type="button" onClick={() => this.unblock(row.original)}>
              <i className="fa fa-trash"></i> Unblock
            </Button>
          </div>
        ),
      },
    ];

    return (
      <div className="content">
        <Grid fluid>
          <Row>
            <Col md={12}>
              <Card
                title="Seat Blocks"
                content={
                  <div>
                    {this.renderFilters()}
                    {error && <div className="alert alert-danger">{error}</div>}
                    <ReactTable
                      manual
                      columns={columns}
                      data={blocks}
                      pages={pageCount}
                      loading={loading}
                      onFetchData={this.fetchDataWithDebounce}
                      defaultPageSize={criteria.limit}
                      pageSizeOptions={[20, 50, 100, 200]}
                      className="-striped -highlight"
                      noDataText={loading ? "Loading..." : "No seat blocks found"}
                    />
                  </div>
                }
              />
            </Col>
          </Row>
        </Grid>
      </div>
    );
  }
}

export default SeatBlocks;
